import { getCore } from '../../../client/core/getCore';
import { getDefaultClient } from '../../../client/defaultClient';
import { retryOnFail } from '../../../utils/retryOnFail';
import { createApiClient } from '../../apiClient';
import { CLIENT_SDK_NAME } from '../../apiClient/constants';
import { isSignedIn } from '../../auth/isSignedIn';
import { projectSettingsExpirationStorageKeySchema } from './projectSettingsExpirationScheme';

/**
 * Expiration time of the project settings in milliseconds.
 */
export const PROJECT_SETTINGS_EXPIRATION_TIME = 1000 * 60 * 5; // 5 minutes

/**
 * Fetches and updates the project settings from the API.
 *
 * This function retrieves the latest project configuration settings
 * from Dynamic's servers, including authentication options, enabled chains,
 * and security configurations. The settings are cached for performance.
 *
 * @param [client] - The Dynamic client instance. Only required when using multiple Dynamic clients.
 * @returns A promise that resolves to the updated project settings.
 */
export const fetchProjectSettings = async (client = getDefaultClient()) => {
  const core = getCore(client);

  const currentExpiration = await core.storage.getItem(
    projectSettingsExpirationStorageKeySchema
  );

  const hasProjectSettings = Boolean(client.projectSettings);
  const isExpired = currentExpiration && currentExpiration < Date.now();

  // We want to cache the project settings if its valid and user is connected.
  // This avoids unnecessary API calls and speeds up the initial load.
  if (hasProjectSettings && !isExpired && isSignedIn(client)) {
    return client.projectSettings;
  }

  const apiClient = createApiClient({}, client);

  core.logger.debug('[fetchProjectSettings] Fetching project settings...');

  const doFetch = async () =>
    apiClient.getEnvironmentSettings(
      {
        environmentId: core.environmentId,
        sdkVersion: `${CLIENT_SDK_NAME}/${core.version}`,
      },
      {
        credentials: 'omit',
      }
    );

  const projectSettings = await retryOnFail({
    fn: doFetch,
    maxRetries: 2,
  });

  core.state.set({ projectSettings: projectSettings ?? null });

  await core.storage.setItem(
    projectSettingsExpirationStorageKeySchema,
    Date.now() + PROJECT_SETTINGS_EXPIRATION_TIME
  );

  return projectSettings;
};
